import React from "react";
import PropTypes from "prop-types";
import Select, { components } from "react-select";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";
import styles from "./SelectInput.module.styl";

function DropdownIndicator(props, name) {
    return (
        <components.DropdownIndicator {...props}>
            <ArrowIcon className={styles.icon} data-test-name={`${name}_select`} />
        </components.DropdownIndicator>
    );
}

function Option(props, name) {
    const { label, data, isSelected } = props;
    return (
        <components.Option {...props}>
            <span
                data-test-name={name}
                data-test-value={data.value}
                data-test-selected={isSelected}
            >
                {label}
            </span>
        </components.Option>
    );
}

function SelectInput({
    options,
    selectedValue,
    onChange,
    name,
    isInvalid,
    placeholder,
    disabled,
    isMulti,
    noOptionsMessage,
    padding,
    ...props
}) {
    const selectStyles = {
        control: (provided, state) => ({
            ...provided,
            boxShadow: "none",
            borderColor: styles.colorForm,
            "&:hover": {
                borderColor: styles.colorDarkGray,
            },
            ...(state.isFocused && {
                borderColor: styles.colorDarkGray,
            }),
            ...(isInvalid && {
                borderColor: styles.colorRed,
            }),
        }),
        menuList: (provided) => ({
            ...provided,
            "::-webkit-scrollbar": {
                width: "5px",
                background: styles.colorWhite,
            },
            "::-webkit-scrollbar-track": {
                background: styles.colorWhite,
            },
            "::-webkit-scrollbar-thumb": {
                borderRadius: "10px",
                background: styles.colorForm,
            },
        }),
        dropdownIndicator: (provided, state) => ({
            ...provided,
            transform: state.selectProps.menuIsOpen && "rotate(180deg)",
        }),
        valueContainer: (provided) => ({
            ...provided,
            padding: padding === "small" ? "7px 30px 5px 14px" : "12px 30px 10px 14px",
        }),
        singleValue: (provided) => ({
            ...provided,
            margin: "0",
            color: styles.colorDarkGray,
        }),
        input: (provided) => ({
            ...provided,
            margin: "0",
            padding: "0",
        }),
        option: (provided, state) => ({
            ...provided,
            opacity: state.isDisabled ? 0.7 : 1,
            color: styles.colorDarkGray,
            backgroundColor: styles.colorWhite,
            ...(!state.isDisabled && {
                "&:hover": {
                    backgroundColor: styles.colorDarkGray,
                    color: styles.colorWhite,
                },
            }),
        }),
    };

    const defaultValue =
        selectedValue !== undefined &&
        (isMulti
            ? options.filter((opt) => selectedValue.includes(opt.value))
            : options.find((opt) => opt.value === selectedValue));

    const handleChange = (newSelectValue) => {
        const value = isMulti ? newSelectValue.map((v) => v.value) : newSelectValue?.value;
        onChange(value);
    };

    return (
        <Select
            {...props}
            className={styles.container}
            styles={selectStyles}
            name={name}
            placeholder={placeholder}
            noOptionsMessage={() => noOptionsMessage}
            isDisabled={disabled}
            components={{
                Option: Option(props, name),
                IndicatorSeparator: () => null,
                DropdownIndicator: DropdownIndicator(props, name),
            }}
            isMulti={isMulti}
            value={defaultValue ?? null}
            options={options}
            isOptionDisabled={(opt) => opt.disabled}
            onChange={handleChange}
            menuPlacement="auto"
        />
    );
}

SelectInput.propTypes = {
    options: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            label: PropTypes.string.isRequired,
            disabled: PropTypes.bool,
        }),
    ).isRequired,

    isMulti: PropTypes.bool,

    selectedValue: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.arrayOf(PropTypes.string),
        PropTypes.arrayOf(PropTypes.number),
    ]),
    onChange: PropTypes.func.isRequired,

    name: PropTypes.string,
    placeholder: PropTypes.string,
    noOptionsMessage: PropTypes.string,

    disabled: PropTypes.bool,

    isInvalid: PropTypes.bool,

    padding: PropTypes.oneOf(["small", "big"]),
};

SelectInput.defaultProps = {
    noOptionsMessage: "Нет вариантов для выбора",
    padding: "big",
    placeholder: "",
};

export default SelectInput;
